import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { apiPost, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

export function LoginScreen(): ReactElement {
  const { refresh } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await apiPost<{ username: string; mustResetPassword: boolean; height_cm: number | null }>(
        '/api/auth/login',
        { username, password },
      );
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setError('Invalid username or password');
      } else {
        setError(e instanceof ApiError ? e.message : 'Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="flex min-h-dvh items-center justify-center p-6"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-main)' }}
      data-testid="login-screen"
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border p-6 shadow-sm"
        style={{
          background: 'var(--bg-surface)',
          borderColor: 'var(--border)',
        }}
      >
        <header className="flex flex-col items-center gap-2 text-center">
          <div className="text-5xl" aria-hidden="true">👨‍🍳</div>
          <h1 className="text-2xl font-semibold">ChefCook Sign In</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Enter your credentials to continue.
          </p>
        </header>

        {error ? (
          <p
            role="alert"
            className="text-sm"
            style={{ color: '#b91c1c' }}
          >
            {error}
          </p>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          <span>Username</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="min-h-[48px] rounded-md font-semibold disabled:opacity-60"
          style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
        >
          {submitting ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </main>
  );
}
